/**
 * Studio - Music Track Sync Page
 * Create and sync drum tracks with YouTube videos
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  Braces,
  Check,
  CheckCircle2,
  Clipboard,
  Download,
  Gauge,
  Loader2,
  Music2,
  Pause,
  Play,
  RefreshCw,
  Save,
  SlidersHorizontal,
  Video,
  Upload,
  Wand2,
  XCircle,
} from "lucide-react";
import type { ScoreV2, DrumlyExportV1 } from "./types";
import { buildTempoIndex, tickToMsFast, lastTickOf, maskAtMs } from "./tempoUtils";
import { parseSongsterrDrumsToScoreV2 } from "./parser";
import { buildDrumlyExportV1, downloadJson } from "./exportBuilder";
import {
  parseYoutubeVideoId,
  ensureYoutubeIframeApi,
  setPlaybackRateSafe,
  sampleYtMsMedian,
} from "./youtubeUtils";
import FallingNotesStage from "./components/FallingNotesStage";
import TimelineEditor from "./components/TimelineEditor";
import { createSongV2 } from "../songV2/songV2Api";
import "./StudioPage.css";

const APP_VERSION = 10;

const PPQ_BASE = 1920;
const PPQ_MULT = 8;

export default function StudioPage() {
  const navigate = useNavigate();
  const [youtubeUrl, setYoutubeUrl] = useState(
    "https://www.youtube.com/watch?v=0J2QdDbelmY"
  );
  const { videoId, startSec } = useMemo(
    () => parseYoutubeVideoId(youtubeUrl.trim()),
    [youtubeUrl]
  );

  // JSON parsing
  const [jsonText, setJsonText] = useState("");
  const [manualPasteText, setManualPasteText] = useState("");
  const [clipboardError, setClipboardError] = useState<string>("");
  const [showManualPaste, setShowManualPaste] = useState(false);
  const [parseError, setParseError] = useState<string>("");
  const [parseOk, setParseOk] = useState(false);

  const [scoreLive, setScoreLive] = useState<ScoreV2 | null>(null);
  const tempoIndexLive = useMemo(
    () => (scoreLive ? buildTempoIndex(scoreLive.tempo_map ?? [], scoreLive.ppq) : []),
    [scoreLive]
  );

  const firstChartEventMs = useMemo(() => {
    if (!scoreLive || !scoreLive.events?.length) return 0;
    return tickToMsFast(scoreLive.events[0].t0, tempoIndexLive, scoreLive.ppq);
  }, [scoreLive, tempoIndexLive]);

  const trackObjRef = useRef<any>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Sync + position
  const [syncMs, setSyncMs] = useState(0);
  const [posMs, setPosMs] = useState(0);
  const [ytMsNow, setYtMsNow] = useState(0);
  const [previewedOnce, setPreviewedOnce] = useState(false);
  const [calibrationNoteNumber, setCalibrationNoteNumber] = useState(1);
  const [calibrationVideoTime, setCalibrationVideoTime] = useState("0:00.000");
  const [calibrationError, setCalibrationError] = useState("");

  // Export + preview mode
  const [exportJson, setExportJson] = useState<DrumlyExportV1 | null>(null);
  const exportJsonRef = useRef<DrumlyExportV1 | null>(null); // For YouTube callback closure
  const [previewMode, setPreviewMode] = useState<"live" | "export">("live");
  const [needsRestart, setNeedsRestart] = useState(false);

  // Timeline Editor - düzenlenen events (henüz kaydedilmemiş)
  const [editedEvents, setEditedEvents] = useState<Array<{ ms: number; m: number }> | null>(null);
  const [timelineDirty, setTimelineDirty] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [hasCalibrated, setHasCalibrated] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [savedSongId, setSavedSongId] = useState("");

  // UI tuning
  const [lookaheadMs, setLookaheadMs] = useState(2200);
  const [hitMs, setHitMs] = useState(80);

  // YouTube player state
  const ytHostRef = useRef<HTMLDivElement | null>(null);
  const ytPlayerRef = useRef<any>(null);
  const [ytReady, setYtReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const rafRef = useRef<number>(0);

  // Speed
  const [speed, setSpeed] = useState(1.0);

  // Derived active score (live vs export)
  // Export mode'da dt/m array'lerinden score oluştur (Timeline Editor değişikliklerini yansıtır)
  const scoreExport: ScoreV2 | null = useMemo(() => {
    // If there are edited events from Timeline Editor, use them for live preview
    if (editedEvents) {
      const events: Array<{ t0: number; dt: number; m: number }> = [];
      let absMs = 0;
      for (let i = 0; i < editedEvents.length; i++) {
        if (i === 0) {
          absMs = editedEvents[0].ms;
        } else {
          absMs += editedEvents[i].ms;
        }
        events.push({
          t0: absMs,
          dt: editedEvents[i].ms,
          m: editedEvents[i].m || 0,
        });
      }
      return {
        ppq: 1,
        tempo_map: [{ tick: 0, bpm: 60000 }],
        events,
      };
    }
    
    if (!exportJson?.dt || !exportJson?.m) return null;
    
    // dt/m array'lerinden events oluştur
    // dt[0] = first event absolute ms, dt[i] = delta from previous
    const events: Array<{ t0: number; dt: number; m: number }> = [];
    let absMs = 0;
    for (let i = 0; i < exportJson.dt.length; i++) {
      if (i === 0) {
        absMs = exportJson.dt[0];
      } else {
        absMs += exportJson.dt[i];
      }
      events.push({
        t0: absMs, // ms cinsinden (ppq=1, bpm=60000 ile 1 tick = 1 ms)
        dt: exportJson.dt[i],
        m: exportJson.m[i] || 0,
      });
    }
    
    // ppq=1, bpm=60000 → tickToMs hesabı: (tick / 1) * (60000 / 60000) = tick
    // Yani t0 değeri direkt ms olarak kullanılır
    return {
      ppq: 1,
      tempo_map: [{ tick: 0, bpm: 60000 }],
      events,
    };
  }, [exportJson, editedEvents]);

  // Keep ref in sync with state for YouTube callback closure
  useEffect(() => {
    exportJsonRef.current = exportJson;
  }, [exportJson]);

  const activeScore = previewMode === "export" ? scoreExport : scoreLive;
  const activeTempoIndex = useMemo(
    () => (activeScore ? buildTempoIndex(activeScore.tempo_map ?? [], activeScore.ppq) : []),
    [activeScore]
  );

  const durationMs = useMemo(() => {
    if (!activeScore) return 0;
    return tickToMsFast(lastTickOf(activeScore), activeTempoIndex, activeScore.ppq);
  }, [activeScore, activeTempoIndex]);

  const chartSummary = useMemo(() => {
    if (!scoreLive) return null;
    const eventCount = scoreLive.events.length;
    const bpm = Math.round(scoreLive.tempo_map?.[0]?.bpm ?? 0);
    const duration = durationMs;
    return {
      eventCount,
      bpm,
      duration,
      timeSignature: "4/4",
    };
  }, [scoreLive, durationMs]);

  const noteTimesMs = useMemo(() => {
    if (editedEvents?.length) {
      let absoluteMs = 0;
      return editedEvents.map((event, index) => {
        absoluteMs = index === 0 ? event.ms : absoluteMs + event.ms;
        return absoluteMs;
      });
    }
    if (!scoreLive) return [];
    return scoreLive.events.map((event) =>
      Math.round(tickToMsFast(event.t0, tempoIndexLive, scoreLive.ppq))
    );
  }, [editedEvents, scoreLive, tempoIndexLive]);

  const selectedCalibrationIndex = Math.max(
    0,
    Math.min(noteTimesMs.length - 1, calibrationNoteNumber - 1)
  );
  const selectedNoteChartMs = noteTimesMs[selectedCalibrationIndex] || 0;

  const statusLabel = useMemo(() => {
    if (isSaved) return "Saved";
    if (exportJson) return "Ready to Save";
    if (hasCalibrated) return "Synced";
    if (ytReady) return "Video Ready";
    if (scoreLive) return "Chart Imported";
    return "Draft";
  }, [isSaved, exportJson, hasCalibrated, ytReady, scoreLive]);

  const statusColor = useMemo(() => {
    switch (statusLabel) {
      case "Saved":
        return "var(--accent-success)";
      case "Ready to Save":
        return "var(--accent-warning)";
      case "Synced":
        return "var(--accent-primary)";
      case "Video Ready":
        return "var(--accent-info)";
      case "Chart Imported":
        return "var(--accent-success)";
      default:
        return "var(--text-muted)";
    }
  }, [statusLabel]);

  const activeMask = useMemo(() => {
    if (!activeScore) return 0;
    return maskAtMs(activeScore, activeTempoIndex, posMs);
  }, [activeScore, activeTempoIndex, posMs]);

  // Auto-parse JSON (debounced)
  useEffect(() => {
    const t = setTimeout(() => {
      setPreviewedOnce(false);
      setHasCalibrated(false);
      setExportJson(null);
      setTimelineDirty(false);

      const txt = jsonText.trim();
      if (!txt) {
        setParseOk(false);
        setParseError("");
        setScoreLive(null);
        setEditedEvents(null);
        trackObjRef.current = null;
        return;
      }

      try {
        const obj = JSON.parse(txt);
        trackObjRef.current = obj;

        const bpm = Number(obj?.bpm || 120);
        const quarterMs = 60000 / (bpm > 0 ? bpm : 120);
        const sixteenthMs = quarterMs / 4;
        const autoFlashMs = Math.round(sixteenthMs * 0.8);

        const sc = parseSongsterrDrumsToScoreV2(obj, {
          ppqBase: PPQ_BASE,
          ppqMultiplier: PPQ_MULT,
          flashMs: autoFlashMs,
        });
        setScoreLive(sc);
        setParseOk(true);
        setParseError("");

        if (sc.events.length) {
          const ti = buildTempoIndex(sc.tempo_map, sc.ppq);
          const parsedEvents = sc.events.map((event, index) => {
            const ms = Math.round(tickToMsFast(event.t0, ti, sc.ppq));
            if (index === 0) {
              return { ms, m: event.m };
            }
            const prevMs = Math.round(tickToMsFast(sc.events[index - 1].t0, ti, sc.ppq));
            return { ms: Math.max(0, ms - prevMs), m: event.m };
          });
          setEditedEvents(parsedEvents);
        } else {
          setEditedEvents(null);
        }
      } catch (e: any) {
        setParseOk(false);
        setParseError(e?.message || "Invalid JSON");
        setScoreLive(null);
        setEditedEvents(null);
        trackObjRef.current = null;
      }
    }, 450);

    return () => clearTimeout(t);
  }, [jsonText]);

  // Build/refresh YouTube player when videoId changes
  useEffect(() => {
    let cancelled = false;

    (async () => {
      await ensureYoutubeIframeApi();
      if (cancelled) return;
      if (!ytHostRef.current) return;

      try {
        ytPlayerRef.current?.destroy?.();
      } catch {}
      ytPlayerRef.current = null;
      setYtReady(false);
      setIsPlaying(false);

      ytPlayerRef.current = new window.YT.Player(ytHostRef.current, {
        width: "100%",
        height: "360",
        videoId,
        playerVars: { playsinline: 1, controls: 1, rel: 0 },
        events: {
          onReady: () => {
            setYtReady(true);
            setPlaybackRateSafe(ytPlayerRef.current, speed);
          },
          onStateChange: (e: any) => {
            const st = e?.data;
            console.log('[YouTube onStateChange] State:', st, 'PLAYING:', window.YT.PlayerState.PLAYING);
            if (st === window.YT.PlayerState.PLAYING) {
              setIsPlaying(true);
              // Auto-export when play is pressed - use ref to get current value
              const currentExportJson = exportJsonRef.current;
              console.log('[Auto-Export] Play detected - exportJson exists:', !!currentExportJson, 'scoreLive exists:', !!scoreLive);
              if (!currentExportJson && scoreLive) {
                console.log('[Auto-Export] Building export with current syncMs:', syncMs);
                buildExport();
              }
            } else {
              setIsPlaying(false);
            }
          },
        },
      });
    })();

    return () => {
      cancelled = true;
      try {
        ytPlayerRef.current?.destroy?.();
      } catch {}
      ytPlayerRef.current = null;
      setYtReady(false);
      setIsPlaying(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  // Apply speed
  useEffect(() => {
    const p = ytPlayerRef.current;
    console.log('[Speed useEffect] Triggered - speed:', speed, 'ytReady:', ytReady, 'player exists:', !!p);
    if (!p || !ytReady) return;
    console.log('[Speed useEffect] Applying speed to YouTube player:', speed);
    setPlaybackRateSafe(p, speed);
  }, [speed, ytReady]);

  // Load a fresh preview state for a new YouTube video and reset export state
  useEffect(() => {
    setExportJson(null);
    setTimelineDirty(false);
    setPreviewMode("live");
    setNeedsRestart(true);
    setPosMs(0);
    setYtMsNow(0);
    setPreviewedOnce(false);
    setHasCalibrated(false);
  }, [videoId]);

  // Auto guess sync offset from YouTube start time if no stored sync exists
  useEffect(() => {
    if (!scoreLive || !videoId) return;

    const stored = localStorage.getItem(`drumly:syncMs:${videoId}`);
    if (stored != null && stored.trim() !== "" && Number.isFinite(Number(stored))) {
      setSyncMs(Math.round(Number(stored)));
      return;
    }

    if (startSec > 0 && firstChartEventMs >= 0) {
      const guess = Math.round(startSec * 1000 - firstChartEventMs);
      setSyncMs(guess);
      return;
    }

    setSyncMs(0);
  }, [scoreLive, videoId, startSec, firstChartEventMs]);

  // RAF: posMs = ytMs - syncMs
  useEffect(() => {
    if (!isPlaying) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    const loop = () => {
      const p = ytPlayerRef.current;
      if (p && p.getCurrentTime) {
        const ytMs = Number(p.getCurrentTime() || 0) * 1000;
        setYtMsNow(ytMs);
        setPosMs(ytMs - syncMs);
      }
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isPlaying, syncMs]);

  // Controls
  function restartToBeginningTab0() {
    const p = ytPlayerRef.current;
    if (!p || !p.seekTo) return;

    const ytMsTarget = Math.max(0, syncMs);
    p.seekTo(ytMsTarget / 1000, true);
    setPosMs(0);
    setYtMsNow(ytMsTarget);
    setNeedsRestart(false);
  }

  function togglePlay() {
    const p = ytPlayerRef.current;
    if (!p || !ytReady) return;
    if (!activeScore || !activeScore.events.length) return;

    if (isPlaying) {
      p.pauseVideo?.();
      return;
    }

    setPreviewedOnce(true);
    if (needsRestart) {
      restartToBeginningTab0();
      setTimeout(() => p.playVideo?.(), 60);
    } else {
      p.playVideo?.();
    }
  }

  // buildExport fonksiyonu
  const buildExport = useCallback((useSyncMs?: number) => {
    const p = ytPlayerRef.current;
    if (!p) return;
    const scoreToExport = scoreExport || scoreLive;
    if (!scoreToExport) return;

    const vd = p.getVideoData?.() || {};
    const ytTitle = String(vd.title || "");
    const ytArtist = String(vd.author || "");
    const ytDurationSec = Number(p.getDuration?.() || 0);
    const exportSyncMs = useSyncMs !== undefined ? useSyncMs : syncMs;

    const exportTempoIndex =
      scoreToExport === scoreExport
        ? buildTempoIndex(scoreToExport.tempo_map ?? [], scoreToExport.ppq)
        : tempoIndexLive;
    const out = buildDrumlyExportV1({
      score: scoreToExport,
      tempoIndex: exportTempoIndex,
      syncMs: exportSyncMs,
      youtubeUrl,
      videoId,
      startSec,
      trackObj: trackObjRef.current,
      ytTitle,
      ytArtist,
      ytDurationSec,
      lookaheadMs,
      hitMs,
      calibrateSpeed: speed,
    });

    setExportJson(out);
    setTimelineDirty(false);
    setPreviewMode("export");
    setNeedsRestart(true);
  }, [scoreExport, scoreLive, tempoIndexLive, syncMs, youtubeUrl, videoId, startSec, lookaheadMs, hitMs, speed]);

  const handleSyncMsChange = useCallback((newSyncMs: number) => {
    setSyncMs(newSyncMs);
    localStorage.setItem(`drumly:syncMs:${videoId}`, String(newSyncMs));
    if (exportJson) {
      setExportJson({ ...exportJson, sync_ms: newSyncMs });
    }
  }, [videoId, exportJson]);

  const adjustSync = useCallback((amount: number) => {
    handleSyncMsChange(syncMs + amount);
    setHasCalibrated(true);
    setNeedsRestart(true);
  }, [handleSyncMsChange, syncMs]);

  const formatDuration = (ms: number) => {
    const totalSec = Math.round(ms / 1000);
    const minutes = Math.floor(totalSec / 60);
    const seconds = totalSec % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const formatPreciseTime = (ms: number) => {
    const safeMs = Math.max(0, Math.round(ms));
    const minutes = Math.floor(safeMs / 60000);
    const seconds = Math.floor((safeMs % 60000) / 1000);
    const milliseconds = safeMs % 1000;
    return `${minutes}:${seconds.toString().padStart(2, "0")}.${milliseconds
      .toString()
      .padStart(3, "0")}`;
  };

  const parsePreciseTime = (value: string) => {
    const trimmed = value.trim().replace(",", ".");
    if (!trimmed) return Number.NaN;
    if (!trimmed.includes(":")) {
      const seconds = Number(trimmed);
      return Number.isFinite(seconds) ? Math.round(seconds * 1000) : Number.NaN;
    }
    const parts = trimmed.split(":");
    if (parts.length !== 2) return Number.NaN;
    const minutes = Number(parts[0]);
    const seconds = Number(parts[1]);
    if (!Number.isFinite(minutes) || !Number.isFinite(seconds) || minutes < 0 || seconds < 0 || seconds >= 60) {
      return Number.NaN;
    }
    return Math.round((minutes * 60 + seconds) * 1000);
  };

  const applyNoteCalibration = (videoTimeMs?: number) => {
    if (!noteTimesMs.length) {
      setCalibrationError("Önce orijinal JSON’u yapıştırın.");
      return;
    }
    const targetMs =
      videoTimeMs === undefined ? parsePreciseTime(calibrationVideoTime) : videoTimeMs;
    if (!Number.isFinite(targetMs) || targetMs < 0) {
      setCalibrationError("Video zamanını 15.250 veya 0:15.250 biçiminde girin.");
      return;
    }
    const newSyncMs = Math.round(targetMs - selectedNoteChartMs);
    handleSyncMsChange(newSyncMs);
    setCalibrationVideoTime(formatPreciseTime(targetMs));
    setCalibrationError("");
    setHasCalibrated(true);
    setNeedsRestart(true);
    setExportJson(null);
    setTimelineDirty(false);
  };

  const useCurrentVideoTime = () => {
    const player = ytPlayerRef.current;
    if (!player?.getCurrentTime) {
      setCalibrationError("YouTube player henüz hazır değil.");
      return;
    }
    const currentMs = Math.round(Number(player.getCurrentTime() || 0) * 1000);
    setCalibrationVideoTime(formatPreciseTime(currentMs));
    applyNoteCalibration(currentMs);
  };

  const parseClipboardJson = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text.trim()) throw new Error("Clipboard is empty.");
      setJsonText(text);
      setClipboardError("");
      setShowManualPaste(false);
    } catch (error: any) {
      setClipboardError(error?.message || "Clipboard access failed.");
      setShowManualPaste(true);
    }
  };

  const handleManualPasteSubmit = () => {
    if (!manualPasteText.trim()) {
      setClipboardError("Please paste valid JSON.");
      return;
    }
    setJsonText(manualPasteText);
    setClipboardError("");
    setShowManualPaste(false);
  };

  const handleJsonUpload = async (file: File) => {
    const text = await file.text();
    setJsonText(text);
    setAdvancedOpen(true);
  };

  const handleEditJson = () => {
    setAdvancedOpen(true);
  };

  const handleCalibrate = async () => {
    const p = ytPlayerRef.current;
    if (!p || !p.getCurrentTime || !scoreLive) return;

    const ytNow = await sampleYtMsMedian(p);
    const newSyncMs = Math.round(ytNow - firstChartEventMs);
    setSyncMs(newSyncMs);
    localStorage.setItem(`drumly:syncMs:${videoId}`, String(newSyncMs));
    setHasCalibrated(true);
    setNeedsRestart(true);
    setExportJson(null);
    setTimelineDirty(false);
  };

  const handleAdvancedToggle = () => {
    setAdvancedOpen((open) => !open);
  };

  function copyExport() {
    if (!exportJson) return;
    navigator.clipboard.writeText(JSON.stringify(exportJson, null, 2));
  }

  function downloadExport() {
    if (!exportJson) return;
    const safeTitle = String(exportJson?.title || "song")
      .replace(/[^\w-]+/g, "_")
      .slice(0, 60);
    downloadJson(`${safeTitle}_drumly_v1.json`, exportJson);
  }

  async function saveToDatabase() {
    if (!exportJson) return;
    if (timelineDirty) {
      setSaveError("Timeline değişikliklerini uygulamak için önce export verisini yeniden oluşturun.");
      return;
    }
    
    try {
      setSaving(true);
      setSaveError("");
      const { source, ...songData } = exportJson;
      const savedSong = await createSongV2({
        ...songData,
        source: {
          type: source.type,
          url: source.url,
          video_id: source.video_id,
        },
        calibrate_speed: exportJson.calibrate_speed || 1.0,
      });
      setIsSaved(true);
      setSavedSongId(savedSong.songv2_id);
    } catch (error: any) {
      console.error("Save failed:", error);
      setSaveError(error.response?.data?.message || error.message || "Şarkı kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  }

  const canPlay = Boolean(ytReady && activeScore?.events?.length && hasCalibrated);

  return (
    <div className="studio-page" style={{ minHeight: "100vh", background: "var(--bg-primary)", padding: "var(--spacing-xl)" }}>
      <div className="studio-shell" style={{ maxWidth: 1600, margin: "0 auto" }}>
        <div style={{
          marginBottom: "var(--spacing-2xl)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--spacing-sm)",
        }} className="studio-hero">
          <h1
            style={{
              fontSize: "var(--font-size-3xl)",
              fontWeight: "var(--font-weight-heavy)",
              color: "var(--text-primary)",
              margin: 0,
              display: "flex",
              alignItems: "center",
              gap: "var(--spacing-md)",
            }}
          >
            <span className="studio-title-icon"><Music2 /></span> Drumly Studio
            <span style={{ fontSize: "var(--font-size-xs)", color: "var(--text-muted)", fontWeight: "var(--font-weight-medium)" }}>v{APP_VERSION}</span>
          </h1>
          <p style={{ color: "var(--text-secondary)", fontSize: "var(--font-size-base)", margin: 0 }}>
            Songsterr davul chart’larını içe aktarın, YouTube ile milisaniye hassasiyetinde senkronlayın ve doğrudan V2 kataloğa kaydedin.
          </p>
          <div style={{ 
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "var(--spacing-sm)",
          }}>
            <div className={parseOk ? "studio-step studio-step-done" : "studio-step"} style={{ padding: "var(--spacing-sm)", borderRadius: "var(--radius-md)", background: "rgba(255,255,255,0.04)" }}>
              <strong>1.</strong> Chart aktar
            </div>
            <div className={hasCalibrated ? "studio-step studio-step-done" : "studio-step"} style={{ padding: "var(--spacing-sm)", borderRadius: "var(--radius-md)", background: "rgba(255,255,255,0.04)" }}>
              <strong>2.</strong> Videoyu senkronla
            </div>
            <div className={previewedOnce ? "studio-step studio-step-done" : "studio-step"} style={{ padding: "var(--spacing-sm)", borderRadius: "var(--radius-md)", background: "rgba(255,255,255,0.04)" }}>
              <strong>3.</strong> Önizle
            </div>
            <div className={isSaved ? "studio-step studio-step-done" : "studio-step"} style={{ padding: "var(--spacing-sm)", borderRadius: "var(--radius-md)", background: "rgba(255,255,255,0.04)" }}>
              <strong>4.</strong> V2 kataloğa kaydet
            </div>
          </div>
        </div>

        {(saveError || savedSongId) && (
          <div className={`studio-feedback ${saveError ? "studio-feedback-error" : "studio-feedback-success"}`}>
            {saveError ? <XCircle /> : <CheckCircle2 />}
            <div>
              <strong>{saveError ? "Kayıt başarısız" : "Şarkı V2 kataloğa kaydedildi"}</strong>
              <span>{saveError || `songv2_id: ${savedSongId}`}</span>
            </div>
            {!saveError && (
              <button onClick={() => navigate(`/songs/${encodeURIComponent(savedSongId)}`)}>Şarkıyı aç</button>
            )}
          </div>
        )}

        <div
          className="studio-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: "var(--spacing-lg)",
            marginBottom: "var(--spacing-xl)",
          }}
        >
          <section
            className="glass-card"
            style={{
              background: "var(--surface-card)",
              border: "1px solid var(--surface-border)",
              borderRadius: "var(--radius-lg)",
              padding: "var(--spacing-lg)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{
              color: "var(--text-secondary)",
              fontWeight: "var(--font-weight-bold)",
              marginBottom: "var(--spacing-sm)",
              fontSize: "var(--font-size-sm)",
            }}>
              <span className="studio-step-number">1</span>
              Orijinal Songsterr JSON
            </div>
            <div style={{ display: "grid", gap: "var(--spacing-base)" }}>
              <label className="studio-json-field">
                <span>JSON’U BURAYA YAPIŞTIRIN</span>
                <textarea
                  value={jsonText}
                  onChange={(event) => setJsonText(event.target.value)}
                  placeholder={'{\n  "name": "Drums",\n  "measures": [ ... ]\n}'}
                  spellCheck={false}
                />
                <small>
                  Verdiğiniz örnekteki gibi <code>name</code> ve <code>measures</code> alanlarını içeren JSON’un tamamını yapıştırın.
                </small>
              </label>
              <div style={{ display: "flex", gap: "var(--spacing-sm)", flexWrap: "wrap" }}>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="btn btn-secondary"
                  style={{
                    borderRadius: "var(--radius-md)",
                    background: "var(--surface-border)",
                    border: "none",
                    color: "var(--text-primary)",
                    padding: "var(--spacing-sm) var(--spacing-base)",
                    cursor: "pointer",
                    fontSize: "var(--font-size-sm)",
                  }}
                >
                  <Upload size={16} /> JSON yükle
                </button>
                <button
                  onClick={parseClipboardJson}
                  className="btn btn-secondary"
                  style={{
                    borderRadius: "var(--radius-md)",
                    background: "transparent",
                    border: "1px solid var(--surface-border)",
                    color: "var(--text-secondary)",
                    padding: "var(--spacing-sm) var(--spacing-base)",
                    cursor: "pointer",
                    fontSize: "var(--font-size-sm)",
                  }}
                >
                  <Clipboard size={16} /> Panodan yapıştır
                </button>
                <button
                  onClick={handleEditJson}
                  className="btn btn-secondary"
                  style={{
                    borderRadius: "var(--radius-md)",
                    background: "transparent",
                    border: "1px solid var(--surface-border)",
                    color: "var(--text-secondary)",
                    padding: "var(--spacing-sm) var(--spacing-base)",
                    cursor: "pointer",
                    fontSize: "var(--font-size-sm)",
                  }}
                >
                  <Braces size={16} /> Gelişmiş JSON görünümü
                </button>
                <input
                  type="file"
                  accept="application/json"
                  hidden
                  ref={fileInputRef}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) handleJsonUpload(file);
                  }}
                />
              </div>

              {showManualPaste && (
                <div style={{ display: "grid", gap: "var(--spacing-sm)", marginTop: "var(--spacing-sm)" }}>
                  <textarea
                    value={manualPasteText}
                    onChange={(e) => setManualPasteText(e.target.value)}
                    placeholder="Paste Songsterr JSON here"
                    style={{
                      width: "100%",
                      minHeight: 140,
                      borderRadius: "var(--radius-md)",
                      background: "rgba(0, 0, 0, 0.12)",
                      border: "1px solid var(--surface-border)",
                      padding: "var(--spacing-sm)",
                      color: "var(--text-primary)",
                      fontFamily: "var(--font-mono)",
                      fontSize: "var(--font-size-sm)",
                    }}
                  />
                  <button
                    onClick={handleManualPasteSubmit}
                    className="btn btn-primary"
                    style={{
                      alignSelf: "flex-start",
                      borderRadius: "var(--radius-md)",
                      background: "var(--accent-primary)",
                      border: "none",
                      color: "white",
                      padding: "var(--spacing-sm) var(--spacing-base)",
                      cursor: "pointer",
                      fontSize: "var(--font-size-sm)",
                    }}
                  >
                    <Check size={16} /> JSON’u ana alana aktar
                  </button>
                </div>
              )}

              {(clipboardError || parseError) && (
                <div style={{ color: "var(--accent-danger)", fontSize: "var(--font-size-xs)", lineHeight: 1.5, marginTop: "var(--spacing-sm)" }}>
                  {clipboardError || parseError}
                </div>
              )}

              <div style={{ color: "var(--text-muted)", fontSize: "var(--font-size-xs)", lineHeight: 1.5 }}>
                {parseOk ? (
                  <span style={{ color: "var(--accent-success)", fontWeight: "var(--font-weight-bold)" }}>
                    ✓ JSON okundu: {scoreLive?.events?.length ?? 0} nota bulundu
                  </span>
                ) : parseError ? (
                  <span style={{ color: "var(--accent-danger)", fontWeight: "var(--font-weight-bold)" }}>
                    ✕ {parseError}
                  </span>
                ) : (
                  <span>Başlamak için yukarıdaki büyük alana orijinal JSON’u yapıştırın.</span>
                )}
              </div>

              <div style={{ color: "var(--text-muted)", fontSize: "var(--font-size-xs)", lineHeight: 1.5 }}>
                JSON değiştiğinde yaklaşık yarım saniye içinde otomatik olarak yeniden işlenir.
              </div>
            </div>
          </section>

          <section
            className="glass-card"
            style={{
              background: "var(--surface-card)",
              border: "1px solid var(--surface-border)",
              borderRadius: "var(--radius-lg)",
              padding: "var(--spacing-lg)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{
              color: "var(--text-secondary)",
              fontWeight: "var(--font-weight-bold)",
              marginBottom: "var(--spacing-sm)",
              fontSize: "var(--font-size-sm)",
            }}>
              <span className="studio-step-number">2</span>
              YouTube videosu ve senkronizasyon
            </div>
            <div style={{ display: "grid", gap: "var(--spacing-base)" }}>
              <label style={{ display: "grid", gap: 6 }}>
                <div style={{ color: "var(--text-muted)", fontSize: "var(--font-size-xs)", fontWeight: "var(--font-weight-bold)" }}>
                  YOUTUBE LİNKİNİ BURAYA YAPIŞTIRIN
                </div>
                <input
                  type="url"
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  style={{
                    width: "100%",
                    borderRadius: "var(--radius-md)",
                    background: "rgba(0, 0, 0, 0.15)",
                    border: "1px solid var(--surface-border)",
                    color: "var(--text-primary)",
                    padding: "var(--spacing-sm)",
                    fontFamily: "var(--font-mono)",
                    fontSize: "var(--font-size-sm)",
                  }}
                />
              </label>

              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ color: "var(--text-muted)", fontSize: "var(--font-size-xs)", fontWeight: "var(--font-weight-bold)" }}>
                  Video kaynağı
                </div>
                <div style={{ color: videoId ? "var(--text-primary)" : "var(--text-muted)", fontSize: "var(--font-size-sm)" }}>
                  {videoId ? `Video hazır: ${videoId}` : "Geçerli bir YouTube bağlantısı girin."}
                </div>
              </div>

              <div className="studio-player">
                <div className="studio-player-head">
                  <span><Video size={15} /> YouTube Player</span>
                  <small className={ytReady ? "is-ready" : ""}>{ytReady ? "HAZIR" : "YÜKLENİYOR"}</small>
                </div>
                <div className="studio-player-frame">
                  <div ref={ytHostRef} />
                </div>
                <div className="studio-player-controls">
                  <button className="studio-play-button" onClick={togglePlay} disabled={!canPlay}>
                    {isPlaying ? <Pause size={17} /> : <Play size={17} />}
                    {isPlaying ? "Duraklat" : "Önizlet"}
                  </button>
                  <label className="studio-speed">
                    <span><Gauge size={14} /> Hız <strong>{speed.toFixed(2)}×</strong></span>
                    <input
                      type="range"
                      min={0.25}
                      max={2}
                      step={0.05}
                      value={speed}
                      disabled={!ytReady}
                      onChange={(event) => setSpeed(Number(event.target.value))}
                    />
                  </label>
                </div>
                <div className="studio-telemetry">
                  <span><small>Video</small><strong>{Math.round(ytMsNow)} ms</strong></span>
                  <span><small>Chart</small><strong>{Math.round(posMs)} ms</strong></span>
                  <span><small>Drift</small><strong className={Math.abs(ytMsNow - posMs - syncMs) < 50 ? "is-good" : "is-warning"}>{ytMsNow > 0 ? Math.round(ytMsNow - posMs - syncMs) : 0} ms</strong></span>
                </div>
              </div>

              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ color: "var(--text-muted)", fontSize: "var(--font-size-xs)", fontWeight: "var(--font-weight-bold)" }}>
                  Senkronizasyon durumu
                </div>
                <div style={{ display: "flex", gap: "var(--spacing-sm)", flexWrap: "wrap", alignItems: "center" }}>
                  <span style={{ color: "var(--text-primary)", fontWeight: "var(--font-weight-bold)" }}>
                    {hasCalibrated ? `Offset: ${syncMs} ms` : "Kalibrasyon gerekli"}
                  </span>
                  <span style={{ color: statusColor, fontWeight: "var(--font-weight-bold)" }}>{statusLabel}</span>
                </div>
              </div>

              <button
                onClick={handleCalibrate}
                disabled={!parseOk || !videoId || !ytReady}
                className="btn btn-primary"
                style={{
                  borderRadius: "var(--radius-md)",
                  background: parseOk && videoId && ytReady ? "var(--accent-primary)" : "var(--surface-border)",
                  border: "none",
                  color: parseOk && videoId && ytReady ? "white" : "var(--text-muted)",
                  padding: "var(--spacing-sm) var(--spacing-base)",
                  cursor: parseOk && videoId && ytReady ? "pointer" : "not-allowed",
                  fontSize: "var(--font-size-sm)",
                }}
              >
                <Wand2 size={16} /> Otomatik kalibre et
              </button>

              <div className="studio-note-calibration">
                <header>
                  <div>
                    <strong>Bir notayı referans alarak ayarla</strong>
                    <span>Seçtiğiniz nota videoda tam olarak hangi zamanda vurulsun?</span>
                  </div>
                  <small>{noteTimesMs.length} nota</small>
                </header>
                <div className="studio-calibration-fields">
                  <label>
                    <span>NOTA NO</span>
                    <input
                      type="number"
                      min={1}
                      max={Math.max(1, noteTimesMs.length)}
                      value={calibrationNoteNumber}
                      disabled={!noteTimesMs.length}
                      onChange={(event) =>
                        setCalibrationNoteNumber(
                          Math.max(
                            1,
                            Math.min(
                              Math.max(1, noteTimesMs.length),
                              Math.round(Number(event.target.value) || 1)
                            )
                          )
                        )
                      }
                    />
                    <small>Chart zamanı: {formatPreciseTime(selectedNoteChartMs)}</small>
                  </label>
                  <label>
                    <span>VİDEODA VURUŞ ZAMANI</span>
                    <input
                      value={calibrationVideoTime}
                      onChange={(event) => setCalibrationVideoTime(event.target.value)}
                      placeholder="0:15.250"
                    />
                    <small>Örnek: 15.250 veya 0:15.250</small>
                  </label>
                </div>
                {calibrationError && <div className="studio-calibration-error">{calibrationError}</div>}
                <div className="studio-calibration-actions">
                  <button onClick={() => applyNoteCalibration()} disabled={!noteTimesMs.length}>
                    <SlidersHorizontal size={15} /> Bu zamana ayarla
                  </button>
                  <button onClick={useCurrentVideoTime} disabled={!noteTimesMs.length || !ytReady}>
                    <Video size={15} /> Player’ın şu anki zamanını kullan
                  </button>
                </div>
                <p>
                  Sonuç: {calibrationNoteNumber}. nota video
                  <strong> {formatPreciseTime(selectedNoteChartMs + syncMs)}</strong> zamanında vuruş çizgisine gelir.
                </p>
              </div>

              <div className="studio-fine-sync">
                <span><SlidersHorizontal size={14} /> Hassas senkronizasyon</span>
                <div>
                  <button onClick={() => adjustSync(-100)}>-100</button>
                  <button onClick={() => adjustSync(-10)}>-10</button>
                  <input
                    type="number"
                    value={syncMs}
                    onChange={(event) => handleSyncMsChange(Math.round(Number(event.target.value) || 0))}
                    aria-label="Senkronizasyon offset milisaniye"
                  />
                  <button onClick={() => adjustSync(10)}>+10</button>
                  <button onClick={() => adjustSync(100)}>+100</button>
                </div>
              </div>

              <button
                className={`studio-mode-button ${previewMode === "export" ? "is-export" : ""}`}
                onClick={() => setPreviewMode((mode) => mode === "export" ? "live" : "export")}
                disabled={!exportJson}
              >
                <Activity size={15} />
                Nota görünümü: {previewMode === "export" ? "Export" : "Canlı"}
              </button>

              <div style={{ color: "var(--text-muted)", fontSize: "var(--font-size-xs)", lineHeight: 1.5 }}>
                İlk chart notasının vurduğu anda kalibre edin; ardından ±10/100 ms ile hassas ayar yapın.
              </div>
            </div>
          </section>

          <section
            className="glass-card"
            style={{
              background: "var(--surface-card)",
              border: "1px solid var(--surface-border)",
              borderRadius: "var(--radius-lg)",
              padding: "var(--spacing-lg)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{
              color: "var(--text-secondary)",
              fontWeight: "var(--font-weight-bold)",
              marginBottom: "var(--spacing-sm)",
              fontSize: "var(--font-size-sm)",
            }}>
              <span className="studio-step-number">3</span>
              Önizle ve Drumly JSON üret
            </div>
            <div style={{ display: "grid", gap: "var(--spacing-base)" }}>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ color: "var(--text-muted)", fontSize: "var(--font-size-xs)", fontWeight: "var(--font-weight-bold)" }}>
                  Hazırlık durumu
                </div>
                <div style={{ color: canPlay ? "var(--text-primary)" : "var(--text-muted)", fontSize: "var(--font-size-sm)" }}>
                  {canPlay ? "Önizleme için hazır" : "Önce JSON’u yükleyin ve videoyu kalibre edin."}
                </div>
              </div>

              <div style={{ display: "flex", gap: "var(--spacing-sm)", flexWrap: "wrap" }}>
                <button
                  onClick={togglePlay}
                  disabled={!canPlay}
                  className="btn"
                  style={{
                    borderRadius: "var(--radius-md)",
                    background: canPlay ? "var(--accent-success)" : "var(--surface-border)",
                    border: "none",
                    color: canPlay ? "white" : "var(--text-muted)",
                    padding: "var(--spacing-sm) var(--spacing-base)",
                    fontSize: "var(--font-size-sm)",
                    cursor: canPlay ? "pointer" : "not-allowed",
                  }}
                >
                  {isPlaying ? "Önizlemeyi duraklat" : "Önizlemeyi oynat"}
                </button>

                <button
                  onClick={() => buildExport()}
                  disabled={!scoreLive || !hasCalibrated}
                  className="btn btn-secondary"
                  style={{
                    borderRadius: "var(--radius-md)",
                    background: scoreLive ? "var(--accent-primary)" : "var(--surface-border)",
                    border: "none",
                    color: scoreLive ? "white" : "var(--text-muted)",
                    padding: "var(--spacing-sm) var(--spacing-base)",
                    fontSize: "var(--font-size-sm)",
                    cursor: scoreLive ? "pointer" : "not-allowed",
                  }}
                >
                  <RefreshCw size={16} /> Export oluştur
                </button>

                <button
                  onClick={saveToDatabase}
                  disabled={!exportJson || saving || timelineDirty}
                  className="btn btn-success"
                  style={{
                    borderRadius: "var(--radius-md)",
                    background: exportJson ? "var(--accent-success)" : "var(--surface-border)",
                    border: "none",
                    color: exportJson ? "white" : "var(--text-muted)",
                    padding: "var(--spacing-sm) var(--spacing-base)",
                    fontSize: "var(--font-size-sm)",
                    cursor: exportJson && !saving && !timelineDirty ? "pointer" : "not-allowed",
                  }}
                >
                  {saving ? <Loader2 size={16} className="studio-spin" /> : <Save size={16} />}
                  {saving ? "Kaydediliyor…" : "4. V2 kataloğa kaydet"}
                </button>
              </div>

              <div style={{ display: "grid", gap: 6, fontSize: "var(--font-size-xs)", color: "var(--text-muted)" }}>
                {chartSummary ? (
                  <>
                    <div>{chartSummary.eventCount} notes · {chartSummary.duration ? formatDuration(chartSummary.duration) : "—"} duration</div>
                    <div>{chartSummary.bpm} BPM · {chartSummary.timeSignature}</div>
                    <div>Video time: {formatDuration(ytMsNow)} · Track pos: {formatDuration(posMs)}</div>
                  </>
                ) : (
                  <div>Export preview after chart and sync are ready.</div>
                )}
              </div>

              <div className="studio-output">
                <header>
                  <div>
                    <strong>Drumly V2 JSON</strong>
                    <span>{exportJson ? `${exportJson.dt.length} nota · kaydetmeye hazır` : "Henüz oluşturulmadı"}</span>
                  </div>
                  <div>
                    <button onClick={copyExport} disabled={!exportJson}><Clipboard size={14} /> Kopyala</button>
                    <button onClick={downloadExport} disabled={!exportJson}><Download size={14} /> İndir</button>
                  </div>
                </header>
                <pre>
                  {exportJson
                    ? JSON.stringify(exportJson, null, 2)
                    : '// Önce "Export oluştur" butonuna basın.'}
                </pre>
              </div>
            </div>
          </section>
        </div>

        <div style={{ marginTop: "var(--spacing-xl)" }}>
          {activeScore ? (
            <div
              className="glass-card"
              style={{
                background: "var(--surface-card)",
                border: "1px solid var(--surface-border)",
                borderRadius: "var(--radius-lg)",
                padding: "var(--spacing-lg)",
              }}
            >
              <div style={{ 
                color: "var(--text-secondary)", 
                fontWeight: "var(--font-weight-bold)", 
                marginBottom: "var(--spacing-sm)",
                fontSize: "var(--font-size-sm)",
              }}>
                <Music2 size={16} /> Nota önizleme
              </div>
              <FallingNotesStage
                score={activeScore}
                tempoIndex={activeTempoIndex}
                posMs={posMs}
                activeMask={activeMask}
                lookaheadMs={lookaheadMs}
                hitWindowMs={Math.max(10, Math.min(180, hitMs * 0.35))}
                speed={speed}
              />
            </div>
          ) : (
            <div
              className="glass-card"
              style={{
                background: "var(--surface-card)",
                border: "1px solid var(--surface-border)",
                borderRadius: "var(--radius-lg)",
                padding: "var(--spacing-xl)",
                color: "var(--text-muted)",
                textAlign: "center",
              }}
            >
              Parse a chart and sync a YouTube video to preview notes in the Studio.
            </div>
          )}
        </div>

        <div style={{ marginTop: "var(--spacing-lg)", display: "flex", justifyContent: "flex-end" }}>
          <button
            onClick={handleAdvancedToggle}
            className="btn btn-secondary"
            style={{
              borderRadius: "var(--radius-md)",
              background: "transparent",
              border: "1px solid var(--surface-border)",
              color: "var(--text-secondary)",
              padding: "var(--spacing-sm) var(--spacing-base)",
              fontWeight: "var(--font-weight-bold)",
              cursor: "pointer",
              fontSize: "var(--font-size-sm)",
            }}
          >
            {advancedOpen ? "Hide advanced controls" : "Show advanced controls"}
          </button>
        </div>

        {advancedOpen && (
          <div
            className="glass-card"
            style={{
              marginTop: "var(--spacing-lg)",
              background: "var(--surface-card)",
              border: "1px solid var(--surface-border)",
              borderRadius: "var(--radius-lg)",
              padding: "var(--spacing-lg)",
            }}
          >
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "var(--spacing-sm)",
              marginBottom: "var(--spacing-lg)",
            }}>
              <div>
                <div style={{ color: "var(--text-secondary)", fontWeight: "var(--font-weight-bold)", marginBottom: "var(--spacing-xs)", fontSize: "var(--font-size-sm)" }}>
                  <SlidersHorizontal size={16} /> Gelişmiş Studio kontrolleri
                </div>
                <div style={{ color: "var(--text-muted)", fontSize: "var(--font-size-xs)" }}>
                  Edit raw chart JSON, adjust timing, and inspect export output.
                </div>
              </div>
              <div style={{ color: statusColor, fontWeight: "var(--font-weight-bold)", fontSize: "var(--font-size-sm)" }}>
                {statusLabel}
              </div>
            </div>

            <div style={{ display: "grid", gap: "var(--spacing-lg)" }}>
              <div style={{ display: "grid", gap: "var(--spacing-sm)" }}>
                <div style={{ fontSize: "var(--font-size-xs)", color: "var(--text-muted)", fontWeight: "var(--font-weight-bold)" }}>
                  Raw Chart JSON
                </div>
                <textarea
                  value={jsonText}
                  onChange={(e) => setJsonText(e.target.value)}
                  placeholder="Paste or edit JSON here"
                  style={{
                    width: "100%",
                    minHeight: 220,
                    borderRadius: "var(--radius-md)",
                    background: "rgba(0, 0, 0, 0.25)",
                    border: "1px solid var(--surface-border)",
                    padding: "var(--spacing-base)",
                    color: "var(--text-primary)",
                    fontFamily: "var(--font-mono)",
                    fontSize: "var(--font-size-sm)",
                    resize: "vertical",
                  }}
                />
              </div>

              <div style={{ display: "grid", gap: "var(--spacing-sm)" }}>
                <div style={{ fontSize: "var(--font-size-xs)", color: "var(--text-muted)", fontWeight: "var(--font-weight-bold)" }}>
                  Timing & export settings
                </div>
                <div style={{ display: "grid", gap: "var(--spacing-sm)", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
                  <label style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-xs)" }}>
                    <span style={{ fontSize: "var(--font-size-xs)", color: "var(--text-muted)", fontWeight: "var(--font-weight-bold)" }}>
                      Sync offset (ms)
                    </span>
                    <input
                      type="number"
                      value={syncMs}
                      onChange={(e) => {
                        const v = Math.round(Number(e.target.value) || 0);
                        setSyncMs(v);
                        localStorage.setItem(`drumly:syncMs:${videoId}`, String(v));
                      }}
                      style={{
                        borderRadius: "var(--radius-md)",
                        border: "1px solid var(--surface-border)",
                        background: "rgba(0, 0, 0, 0.2)",
                        color: "var(--text-primary)",
                        padding: "var(--spacing-sm)",
                        fontFamily: "var(--font-mono)",
                        fontSize: "var(--font-size-sm)",
                      }}
                    />
                  </label>
                  <label style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-xs)" }}>
                    <span style={{ fontSize: "var(--font-size-xs)", color: "var(--text-muted)", fontWeight: "var(--font-weight-bold)" }}>
                      Tempo preview speed
                    </span>
                    <input
                      type="range"
                      min={0.25}
                      max={2.0}
                      step={0.05}
                      value={speed}
                      onChange={(e) => setSpeed(Number(e.target.value))}
                      style={{ width: "100%" }}
                    />
                    <div style={{ color: "var(--text-muted)", fontSize: "var(--font-size-xs)", fontFamily: "var(--font-mono)" }}>
                      {speed.toFixed(2)}×
                    </div>
                  </label>
                </div>
                <div style={{ display: "grid", gap: "var(--spacing-sm)", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
                  <label style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-xs)" }}>
                    <span style={{ fontSize: "var(--font-size-xs)", color: "var(--text-muted)", fontWeight: "var(--font-weight-bold)" }}>
                      Lookahead (ms)
                    </span>
                    <input
                      type="number"
                      value={lookaheadMs}
                      onChange={(e) =>
                        setLookaheadMs(
                          Math.max(500, Math.min(6000, Number(e.target.value) || 2200))
                        )
                      }
                      style={{
                        borderRadius: "var(--radius-md)",
                        border: "1px solid var(--surface-border)",
                        background: "rgba(0, 0, 0, 0.2)",
                        color: "var(--text-primary)",
                        padding: "var(--spacing-sm)",
                        fontFamily: "var(--font-mono)",
                        fontSize: "var(--font-size-sm)",
                      }}
                    />
                  </label>
                  <label style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-xs)" }}>
                    <span style={{ fontSize: "var(--font-size-xs)", color: "var(--text-muted)", fontWeight: "var(--font-weight-bold)" }}>
                      Hit window (ms)
                    </span>
                    <input
                      type="number"
                      value={hitMs}
                      onChange={(e) =>
                        setHitMs(
                          Math.max(10, Math.min(300, Number(e.target.value) || 80))
                        )
                      }
                      style={{
                        borderRadius: "var(--radius-md)",
                        border: "1px solid var(--surface-border)",
                        background: "rgba(0, 0, 0, 0.2)",
                        color: "var(--text-primary)",
                        padding: "var(--spacing-sm)",
                        fontFamily: "var(--font-mono)",
                        fontSize: "var(--font-size-sm)",
                      }}
                    />
                  </label>
                </div>
              </div>

              <div style={{ display: "grid", gap: "var(--spacing-sm)" }}>
                <div style={{ fontSize: "var(--font-size-xs)", color: "var(--text-muted)", fontWeight: "var(--font-weight-bold)" }}>
                  Export JSON
                </div>
                <div style={{ display: "flex", gap: "var(--spacing-sm)", flexWrap: "wrap" }}>
                  <button
                    onClick={copyExport}
                    disabled={!exportJson}
                    className="btn btn-secondary"
                    style={{
                      borderRadius: "var(--radius-md)",
                      background: "transparent",
                      border: "1px solid var(--surface-border)",
                      color: "var(--text-secondary)",
                      padding: "var(--spacing-sm) var(--spacing-base)",
                      fontWeight: "var(--font-weight-bold)",
                      cursor: exportJson ? "pointer" : "not-allowed",
                      opacity: exportJson ? 1 : 0.5,
                      fontSize: "var(--font-size-sm)",
                    }}
                  >
                    <Clipboard size={16} /> JSON’u kopyala
                  </button>
                  <button
                    onClick={downloadExport}
                    disabled={!exportJson}
                    className="btn btn-secondary"
                    style={{
                      borderRadius: "var(--radius-md)",
                      background: "transparent",
                      border: "1px solid var(--surface-border)",
                      color: "var(--text-secondary)",
                      padding: "var(--spacing-sm) var(--spacing-base)",
                      fontWeight: "var(--font-weight-bold)",
                      cursor: exportJson ? "pointer" : "not-allowed",
                      opacity: exportJson ? 1 : 0.5,
                      fontSize: "var(--font-size-sm)",
                    }}
                  >
                    <Download size={16} /> JSON’u indir
                  </button>
                </div>
                <pre
                  style={{
                    overflow: "auto",
                    maxHeight: 280,
                    minHeight: 200,
                    fontSize: "var(--font-size-xs)",
                    background: "rgba(0, 0, 0, 0.25)",
                    border: "1px solid var(--surface-border)",
                    borderRadius: "var(--radius-md)",
                    padding: "var(--spacing-sm)",
                    color: "var(--text-primary)",
                    fontFamily: "var(--font-mono)",
                    margin: 0,
                  }}
                >
                  {exportJson
                    ? JSON.stringify(exportJson, null, 2)
                    : "// Generate export to see JSON"}
                </pre>
              </div>

              {exportJson && exportJson.dt && exportJson.m && (
                <div style={{ display: "grid", gap: "var(--spacing-sm)" }}>
                  <div className={timelineDirty ? "studio-timeline-head is-dirty" : "studio-timeline-head"}>
                    <span>Timeline Editor</span>
                    {timelineDirty && (
                      <button onClick={() => buildExport()}>
                        Değişiklikleri export’a uygula
                      </button>
                    )}
                  </div>
                  <TimelineEditor
                    events={editedEvents || exportJson.dt.map((dt: number, i: number) => ({
                      ms: dt,
                      m: exportJson.m[i] || 0,
                    }))}
                    syncMs={syncMs}
                    onEventsChange={(newEvents) => {
                      setEditedEvents(newEvents);
                      setTimelineDirty(true);
                    }}
                    onSyncMsChange={handleSyncMsChange}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
